import { Checkbox, Modal, Text, TextInput } from "@mantine/core";
import { useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import Select from "src/components/shared/_FormInputs/Select";

import { usePostApi } from "../../../../../hooks";
import { Flex } from "../../../../../styled";
import { deepClone, getYachtId } from "../../../../../utils";
import { Notification } from "../../../../../utils/Notification";
import MultiSelect from "../../../../shared/_FormInputs/MultiSelect";
import { Button } from "../../../../shared/Button/Button";
import {
  ETankPlanLogbook,
  type ITankPlan,
  orbs,
  tankTypes,
} from "./tankPlans.model";

interface Props {
  opened: boolean;
  close: () => void;
  values?: ITankPlan;
  refetch: () => void;
}

const capacityUnits = ["MT", "m³"];
const logbooks: Array<"bwl" | "mfsbB" | "mfsbA"> = ["bwl", "mfsbA", "mfsbB"];

const TankPlansAddModal = ({ opened, close, values, refetch }: Props) => {
  const { post } = usePostApi();

  const defaultValues = values ?? {
    name: "",
    type: "",
    orb: ["*"],
    bwl: false,
    mfsbA: false,
    mfsbB: false,
    capacity: {
      value: "",
      unit: "m³",
    },
  };
  const methods = useForm({
    values: defaultValues,
  });

  const prevOrbRef = useRef<string[]>(methods.getValues("orb"));

  useEffect(() => {
    prevOrbRef.current = methods.getValues("orb");
  });
  const handleOrbChange = (newValue: string[]) => {
    const prevOrb = deepClone(prevOrbRef.current);

    const sortedItems = newValue.sort((a, b) => {
      const indexA = orbs.findIndex(el => el.value === a);
      const indexB = orbs.findIndex(el => el.value === b);
      return indexA - indexB;
    });
    prevOrbRef.current = newValue;

    if (prevOrb.includes("*") && newValue.length > 1) {
      methods.setValue(
        "orb",
        sortedItems.filter(el => el !== "*"),
      );
    } else {
      methods.setValue("orb", sortedItems);
    }
  };
  const handleSubmit = async (data: any) => {
    data.yachtid = getYachtId();
    await post({
      request: "save-tank",
      body: {
        params: { ...data },
      },
      onSuccess: res => {
        Notification(
          "Success",
          `Tank ${values ? "edited" : "added"} successfully`,
          "green",
        );
        refetch();
        handleClose();
      },
    });
  };

  const handleClose = () => {
    close();
    setTimeout(() => {
      methods.reset();
    }, 200);
  };
  return (
    <Modal
      title="Add Tank Plan"
      opened={opened}
      onClose={handleClose}
      size="lg">
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(handleSubmit)}
          style={{
            minHeight: "550px",
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column",
          }}>
          <Flex direction="column" gap={5}>
            <Select
              control={methods.control}
              name="type"
              data={tankTypes}
              label="Type"
              required={true}
            />
            <TextInput
              {...methods.register("name")}
              label="Name"
              required={true}
            />
            <Flex gap={10}>
              <TextInput
                type="number"
                step="0.001"
                {...methods.register("capacity.value")}
                label="Capacity"
                required={true}
                style={{ flex: 1 }}
              />
              <Select
                control={methods.control}
                name="capacity.unit"
                data={capacityUnits}
                label="Unit"
                style={{ maxWidth: "100px" }}
              />
            </Flex>

            <MultiSelect
              control={methods.control}
              name="orb"
              data={orbs}
              label="Oil Record Book"
              multiple={true}
              onChange={handleOrbChange}
            />
            <Flex direction="column" gap={5} mt={15}>
              <Text fw={200}>Additionally assigned to:</Text>
              {logbooks.map((el, i) => (
                <Flex
                  key={`checkbox-add-${i}`}
                  justify="space-between"
                  w="85%"
                  ml={10}>
                  <Text>{ETankPlanLogbook[el]}</Text>
                  <Checkbox
                    {...methods.register(el)}
                    checked={methods.watch(el)}
                  />
                </Flex>
              ))}
            </Flex>
          </Flex>
          <Flex justify="space-between" gap={10} mt={40}>
            <Button variant="outline" color="red" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit">{values ? "Save" : "Add"}</Button>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};
export default TankPlansAddModal;
